import React, { useState, useEffect } from 'react';
import { supplierService } from '../services/api';
import SupplierForm from '../components/SupplierForm';
import SupplierScreening from '../components/SupplierScreening';
import SupplierDetail from '../components/SupplierDetail';
import { 
  Plus, 
  Search, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building2,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

function SupplierList() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [screeningSupplier, setScreeningSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch suppliers on component load
  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierService.getAll();
      setSuppliers(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load suppliers. Is the Django server running?');
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter suppliers based on search
  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.service_category_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort filtered suppliers
  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'supplier_name':
        aValue = a.supplier_name.toLowerCase();
        bValue = b.supplier_name.toLowerCase();
        break;
      case 'service_category':
        aValue = (a.service_category_name || '').toLowerCase();
        bValue = (b.service_category_name || '').toLowerCase();
        break;
      case 'currency':
        aValue = a.currency;
        bValue = b.currency;
        break;
      case 'approval_status':
        // Custom order: PENDING first, then APPROVED, then REJECTED
        const statusOrder = { 'PENDING': 1, 'APPROVED': 2, 'REJECTED': 3 };
        aValue = statusOrder[a.approval_status] || 4;
        bValue = statusOrder[b.approval_status] || 4;
        break;
      case 'screening_status':
        // Custom order: NOT_STARTED, IN_PROGRESS, VALIDATED
        const screeningOrder = { 'NOT_STARTED': 1, 'IN_PROGRESS': 2, 'VALIDATED': 3 };
        aValue = screeningOrder[a.screening_status] || 0;
        bValue = screeningOrder[b.screening_status] || 0;
        break;
      case 'created_at':
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
      default:
        aValue = a[sortField];
        bValue = b[sortField];
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sortable header component
  const SortableHeader = ({ field, label }) => {
    const isActive = sortField === field;
    
    return (
      <th 
        onClick={() => handleSort(field)}
        style={{
          padding: '14px 16px',
          textAlign: 'left',
          fontSize: '12px',
          fontWeight: '600',
          color: isActive ? '#3b82f6' : '#64748b',
          textTransform: 'uppercase',
          borderBottom: '1px solid #e2e8f0',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {label}
          <span style={{ display: 'flex', flexDirection: 'column', marginLeft: '4px' }}>
            <ChevronUp 
              size={12} 
              color={isActive && sortDirection === 'asc' ? '#3b82f6' : '#cbd5e1'} 
              style={{ marginBottom: '-4px' }}
            />
            <ChevronDown 
              size={12} 
              color={isActive && sortDirection === 'desc' ? '#3b82f6' : '#cbd5e1'} 
            />
          </span>
        </div>
      </th>
    );
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const styles = {
      APPROVED: { bg: '#dcfce7', color: '#166534', icon: CheckCircle },
      REJECTED: { bg: '#fee2e2', color: '#991b1b', icon: XCircle },
      PENDING: { bg: '#fef3c7', color: '#92400e', icon: Clock },
    };
    const style = styles[status] || styles.PENDING;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: style.bg,
        color: style.color,
      }}>
        <Icon size={14} />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <RefreshCw className="spinning" size={40} color="#3b82f6" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1e293b',
            margin: 0 
          }}>
            Supplier Directory
          </h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>
            Manage suppliers and screening processes
          </p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          <Plus size={18} />
          Add Supplier
        </button>
        {/* Supplier Form Modal */}
        {showForm && (
          <SupplierForm
            onClose={() => setShowForm(false)}
            onSuccess={() => {
              fetchSuppliers();
              setShowForm(false);
            }}
          />
        )}
        {/* Screening Modal */}
        {screeningSupplier && (
          <SupplierScreening
            supplier={screeningSupplier}
            onClose={() => {
              fetchSuppliers();
              setScreeningSupplier(null)}
            }
          />
        )}
        {/* Supplier Detail Modal */}
        {selectedSupplier && (
          <SupplierDetail
            supplier={selectedSupplier}
            onClose={() => setSelectedSupplier(null)}
            onUpdate={() => {
              fetchSuppliers();
              setSelectedSupplier(null);
            }}
          />
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '8px',
          marginBottom: '24px',
        }}>
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '24px',
        border: '1px solid #e2e8f0',
      }}>
        <Search size={20} color="#64748b" />
        <input
          type="text"
          placeholder="Search suppliers by name, email, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            fontSize: '14px',
          }}
        />
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {[
          { label: 'Total Suppliers', value: suppliers.length, color: '#3b82f6' },
          { label: 'Approved', value: suppliers.filter(s => s.approval_status === 'APPROVED').length, color: '#22c55e' },
          { label: 'Pending', value: suppliers.filter(s => s.approval_status === 'PENDING').length, color: '#f59e0b' },
          { label: 'Rejected', value: suppliers.filter(s => s.approval_status === 'REJECTED').length, color: '#ef4444' },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
          }}>
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>{stat.label}</p>
            <p style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: stat.color,
              margin: '8px 0 0 0'
            }}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Suppliers Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <SortableHeader field="supplier_name" label="Supplier" />
              <SortableHeader field="service_category" label="Category" />
              <SortableHeader field="currency" label="Currency" />
              <SortableHeader field="approval_status" label="Status" />
              <SortableHeader field="screening_status" label="Screening" />
              <SortableHeader field="created_at" label="Created" />
              <th style={{
                padding: '14px 16px',
                textAlign: 'left',
                fontSize: '12px',
                fontWeight: '600',
                color: '#64748b',
                textTransform: 'uppercase',
                borderBottom: '1px solid #e2e8f0',
              }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSuppliers.length === 0 ? (
              <tr>
                <td colSpan={7} style={{
                  padding: '48px',
                  textAlign: 'center',
                  color: '#64748b',
                }}>
                  <Building2 size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No suppliers found</p>
                </td>
              </tr>
            ) : (
              sortedSuppliers.map((supplier) => (
                <tr 
                  key={supplier.id} 
                  style={{ 
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedSupplier(supplier)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '16px' }}>
                    <div>
                      <p style={{ 
                        fontWeight: '500', 
                        color: '#1e293b',
                        margin: 0 
                      }}>
                        {supplier.supplier_name}
                      </p>
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#64748b',
                        margin: '2px 0 0 0'
                      }}>
                        {supplier.email}
                      </p>
                    </div>
                  </td>
                  <td style={{ padding: '16px', color: '#475569' }}>
                    {supplier.service_category_name || '-'}
                  </td>
                  <td style={{ padding: '16px', color: '#475569' }}>
                    {supplier.currency}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <StatusBadge status={supplier.approval_status} />
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor:
                          supplier.screening_status === 'VALIDATED'
                            ? '#dcfce7'
                            : supplier.screening_status === 'IN_PROGRESS'
                            ? '#fef3c7'
                            : '#f1f5f9',
                        color:
                          supplier.screening_status === 'VALIDATED'
                            ? '#166534'
                            : supplier.screening_status === 'IN_PROGRESS'
                            ? '#92400e'
                            : '#64748b',
                      }}
                    >
                      {supplier.screening_status === 'VALIDATED'
                        ? 'Validated'
                        : supplier.screening_status === 'IN_PROGRESS'
                        ? 'In Progress'
                        : 'Not Started'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', color: '#64748b', fontSize: '13px' }}>
                    {new Date(supplier.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setScreeningSupplier(supplier);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                      }}
                    >
                      Screening
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SupplierList;